import { Download } from "lucide-react";
import { fmtDuration } from "@/lib/api";

const TIER = { hot: "text-rose-400", warm: "text-amber-400", cold: "text-sky-400" };
const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

const exportCsv = (d) => {
  const head = ["Email", "Country", "Assets opened", "Finished", "Total time (s)", "Score", "Tier",
    ...d.assets.flatMap((a) => [`${a.title} — time (s)`, `${a.title} — completion %`])];
  const rows = d.visitors.map((v) => [v.email, v.country, `${v.opened}/${d.assets.length}`, v.finished, v.total_seconds, v.score, v.tier,
    ...d.assets.flatMap((a) => { const c = v.cells[a.id]; return c ? [c.time_seconds, c.completion] : ["", ""]; })]);
  const blob = new Blob([[head, ...rows].map((r) => r.map(csvCell).join(",")).join("\n")], { type: "text/csv" });
  const el = document.createElement("a");
  el.href = URL.createObjectURL(blob);
  el.download = "hub-engagement.csv";
  el.click();
};

const Cell = ({ c, light }) => {
  if (!c || (!c.time_seconds && !c.completion)) return <span className={light ? "text-slate-300" : "text-slate-600"}>—</span>;
  const pct = Math.min(100, c.completion || 0);
  return (
    <div className="min-w-[88px]">
      <div className="flex justify-between text-[11px] font-mono mb-1"><span>{fmtDuration(c.time_seconds)}</span><span className={pct >= 100 ? "text-emerald-500" : ""}>{Math.round(pct)}%</span></div>
      <div className={`h-1.5 rounded-full overflow-hidden ${light ? "bg-slate-100" : "bg-white/10"}`}>
        <div className={`h-full ${pct >= 100 ? "bg-emerald-500" : "bg-indigo-500"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export const HubEngagement = ({ data: d, light = false, testid = "hub-engagement" }) => {
  if (!d) return null;
  const border = light ? "border-slate-200" : "border-[#242C3D]";
  const dim = light ? "text-slate-500" : "text-slate-400";
  return (
    <section data-testid={testid} className={`rounded-lg border ${border} ${light ? "bg-white" : "bg-[#141A24]/60"} mb-12 break-inside-avoid`}>
      <div className={`px-6 py-4 border-b ${border} flex flex-wrap items-center justify-between gap-3`}>
        <div>
          <h2 className="font-display font-semibold text-lg">Hub engagement</h2>
          <p className={`text-xs ${dim}`}>How far each identified visitor got through this campaign's content (time viewed · % completed).</p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`font-mono text-xs ${dim}`} data-testid={`${testid}-count`}>{d.total_visitors} visitors</span>
          {d.visitors.length > 0 && (
            <button type="button" data-testid={`${testid}-csv`} onClick={() => exportCsv(d)}
              className={`h-9 px-4 rounded-full border ${border} text-sm flex items-center gap-2 print:hidden ${light ? "hover:bg-slate-50" : "hover:bg-white/5"}`}><Download className="w-4 h-4" /> CSV</button>
          )}
        </div>
      </div>
      {d.visitors.length === 0 ? <p className={`py-12 text-center text-sm ${dim}`} data-testid={`${testid}-empty`}>No identified visitors yet.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={`text-left font-mono text-[11px] uppercase tracking-wider ${dim} border-b ${border}`}>
                <th className="px-6 py-3 font-normal">Visitor</th><th className="px-3 py-3 font-normal text-right">Opened</th><th className="px-3 py-3 font-normal text-right">Finished</th>
                <th className="px-3 py-3 font-normal text-right">Total time</th><th className="px-3 py-3 font-normal text-right">Score</th>
                {d.assets.map((a) => (
                  <th key={a.id} className="px-3 py-3 font-normal min-w-[120px] max-w-[180px]" title={a.title}>
                    <span className="block truncate normal-case tracking-normal text-xs">{a.title}</span>
                    <span className="block text-[10px]">{a.visitors} viewed · {a.finished} done</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {d.visitors.map((v, i) => (
                <tr key={v.email} data-testid={`${testid}-row-${i}`} className={`border-b ${border} last:border-0`}>
                  <td className="px-6 py-3"><span className="font-medium">{v.email}</span>{v.country && <span className={`block text-xs ${dim}`}>{v.country}</span>}</td>
                  <td className="px-3 py-3 text-right font-mono">{v.opened}/{d.assets.length}</td>
                  <td className="px-3 py-3 text-right font-mono">{v.finished}</td>
                  <td className="px-3 py-3 text-right font-mono">{fmtDuration(v.total_seconds)}</td>
                  <td className={`px-3 py-3 text-right font-mono ${TIER[v.tier]}`}>{v.score}</td>
                  {d.assets.map((a) => <td key={a.id} className="px-3 py-3"><Cell c={v.cells[a.id]} light={light} /></td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};
